import { ArrowLeft, Building2, ExternalLink, Info, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '../components/ui/Button';
import { Container } from '../components/ui/Container';
import { PageHero } from '../components/ui/PageHero';
import { usePageMeta } from '../lib/usePageMeta';

const ICERTS_URL = import.meta.env.VITE_ICERTS_URL ?? 'https://www.icerts.com/get-started-icerts/';

export function InsuranceCertificate(): JSX.Element {
  usePageMeta();

  return (
    <>
      <PageHero
        eyebrow="Owners"
        title="Certificate of Insurance"
        lead="A Certificate of Insurance (COI) confirms the association's master insurance coverage. It is issued through iCerts, the association's certificate service, not by the board. How you get one depends on your situation."
      />

      <section className="section bg-brand-white">
        <Container>
          <div className="max-w-2xl">
            <ul className="space-y-6">
              <li className="rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card md:p-8">
                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <Building2 size={20} strokeWidth={2} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold tracking-heading-tight text-brand-charcoal">
                      Selling or refinancing your unit
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-brand-body-dark">
                      Your title company, closing agent, or lender orders the certificate directly
                      from iCerts. Give them the iCerts link below and they will handle the request.
                      You do not need to order it yourself.
                    </p>
                  </div>
                </div>
              </li>

              <li className="rounded-lg border border-brand-surface bg-brand-white p-6 shadow-card md:p-8">
                <div className="flex items-start gap-4">
                  <span
                    className="mt-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-yellow text-brand-charcoal"
                    aria-hidden="true"
                  >
                    <RefreshCw size={20} strokeWidth={2} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold tracking-heading-tight text-brand-charcoal">
                      Renewing your own policy, or need a copy
                    </h2>
                    <p className="mt-3 text-base leading-relaxed text-brand-body-dark">
                      Send a copy of your insurance renewal letter to iCerts through their site
                      below, and they will reply with instructions to complete the request.
                    </p>
                  </div>
                </div>
              </li>
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button href={ICERTS_URL} target="_blank" rel="noreferrer noopener" size="lg">
                Go to iCerts
                <ExternalLink size={16} aria-hidden="true" />
              </Button>
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-lg bg-brand-surface px-5 py-4">
              <Info
                size={18}
                strokeWidth={2}
                className="mt-0.5 shrink-0 text-brand-yellow-deep"
                aria-hidden="true"
              />
              <p className="text-sm leading-relaxed text-brand-body-dark">
                Please do not create an iCerts account. The iCerts portal is for lenders, title
                companies, and closing agents, not individual owners.
              </p>
            </div>

            <Link
              to="/documents"
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold text-brand-charcoal underline decoration-brand-yellow-deep/40 underline-offset-4 transition-colors hover:decoration-brand-yellow-deep"
            >
              <ArrowLeft size={14} aria-hidden="true" />
              Back to Documents
            </Link>
          </div>
        </Container>
      </section>
    </>
  );
}
